import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Captions, Download, FolderOpen, Loader2, RotateCw, Scissors, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { absUrl, errMsg, finalCutHealth, startFinalCut } from '../../lib/aiStudioApi';
import { finalCutRunning } from './useMovieProject';
import { useElapsed } from './LiveMedia';

function MissingDialog({ missing, onClose, onSkip, onRender }) {
  return (
    <Dialog open={!!missing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="studio-dark max-w-md border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="final-cut-missing-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle size={16} className="text-amber-300" /> {missing?.message}</DialogTitle></DialogHeader>
        <div className="max-h-60 space-y-1 overflow-y-auto">
          {(missing?.missing || []).map((m) => (
            <div key={m.id} className="flex items-center justify-between rounded-md bg-neutral-900 px-3 py-1.5 text-xs" data-testid={`final-cut-missing-${m.id}`}>
              <span className="truncate text-neutral-300"><span className="mr-1.5 font-mono text-amber-300">EP{m.episode || 1}</span>{m.title || 'Untitled shot'}</span>
              {m.rendering ? <span className="flex items-center gap-1 text-amber-200"><Loader2 size={11} className="animate-spin" /> rendering</span> : <span className="text-neutral-500">not rendered</span>}
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onRender} data-testid="final-cut-render-missing-btn">Render them first</Button>
          <Button onClick={onSkip} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="final-cut-skip-missing-btn">Cut without them</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CutStatus({ fc, running, onRetry }) {
  const elapsed = useElapsed(fc.started_at, running);
  if (running) return (
    <div className="space-y-2 rounded-xl border border-amber-400/30 bg-amber-500/5 p-4" data-testid="final-cut-progress">
      <div className="flex justify-between text-sm"><span className="text-amber-100" data-testid="final-cut-step">{fc.step || 'Preparing the edit'}</span><span className="font-mono text-neutral-300" data-testid="final-cut-percent">{fc.progress || 0}%</span></div>
      <Progress value={fc.progress || 0} className="h-2 bg-neutral-800" />
      <div className="flex justify-between text-[11px] text-neutral-500"><span>Elapsed {elapsed} · hard stop at 10:00 — it never loops</span><span>{fc.shots} shots{fc.skipped ? ` · ${fc.skipped} skipped` : ''}</span></div>
    </div>
  );
  if (fc.status === 'failed') return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="final-cut-error">
      <span className="text-sm text-red-200">{fc.error}</span>
      <Button size="sm" onClick={onRetry} className="bg-neutral-100 text-neutral-900 hover:bg-white" data-testid="final-cut-retry-btn"><RotateCw size={13} className="mr-1" /> Retry</Button>
    </div>
  );
  if (fc.status !== 'completed') return null;
  return (
    <div className="space-y-3" data-testid="final-cut-result">
      <video src={absUrl(fc.url)} controls playsInline className="mx-auto max-h-[60vh] w-full rounded-xl border border-neutral-800 bg-black" data-testid="final-cut-player" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-emerald-300" data-testid="final-cut-done-step">{fc.step} · {fc.duration_seconds}s</span>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="outline" className="border-neutral-700 text-neutral-200"><a href={absUrl(fc.url)} download data-testid="final-cut-download-btn"><Download size={13} className="mr-1" /> Download MP4</a></Button>
          <Button asChild size="sm" className="bg-amber-500 text-neutral-900 hover:bg-amber-400"><Link to="/studio-library" data-testid="final-cut-open-library-btn"><FolderOpen size={13} className="mr-1" /> Open in Studio Library</Link></Button>
        </div>
      </div>
    </div>
  );
}

/** Step 7 — Final Cut on Konnect's own server (free, live %, 10-minute hard stop). */
export function FinalCutPanel({ project, onPatch, onChanged, onGoToStep }) {
  const series = project.format === 'series';
  const eps = series ? project.episodes_count || 1 : 1;
  const [episode, setEpisode] = useState(series ? 1 : 0);
  const [health, setHealth] = useState(null);
  const [missing, setMissing] = useState(null);
  const [starting, setStarting] = useState(false);
  const fc = project.final_cut || {};
  const running = finalCutRunning(fc);
  useEffect(() => { finalCutHealth().then(setHealth).catch(() => setHealth({ available: false })); }, []);

  async function start(skip = false) {
    setStarting(true);
    try {
      await startFinalCut(project.id, { episode, skip_missing: skip, burn_subtitles: !!project.burn_subtitles });
      setMissing(null); toast.success('Final Cut started — free, on Konnect’s own server'); onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'missing_shots') setMissing(d); else toast.error(errMsg(e));
    } finally { setStarting(false); }
  }

  return (
    <div className="space-y-4" data-testid="final-cut-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100"><Scissors size={15} className="text-amber-300" /> Final Cut
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-200" data-testid="final-cut-free-badge">FREE · 0 cr</span>
          </div>
          <div className="text-xs text-neutral-500">Joins every shot in order, mixes voices + music, adds subtitles, files it in your Studio Library.</div>
          <div className="flex items-center gap-1.5 text-[11px]" data-testid="final-cut-health">
            <ShieldCheck size={12} className={health?.available ? 'text-emerald-300' : 'text-neutral-500'} />
            <span className={health?.available ? 'text-emerald-300' : 'text-red-300'}>{!health ? 'Checking the video engine…' : health.available ? 'Video engine ready on Konnect’s server' : 'Video engine (FFmpeg) is not installed on this server'}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {series && (
            <select value={episode} onChange={(e) => setEpisode(Number(e.target.value))} className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-100" data-testid="final-cut-episode-select">
              {Array.from({ length: eps }, (_, i) => <option key={i + 1} value={i + 1}>Episode {i + 1}</option>)}
              <option value={0}>Full season</option>
            </select>
          )}
          <label className="flex items-center gap-2 text-xs text-neutral-300"><Captions size={13} />Burn-in subtitles
            <Switch checked={!!project.burn_subtitles} onCheckedChange={(v) => onPatch({ burn_subtitles: v })} data-testid="final-cut-burn-subs-switch" />
          </label>
          <Button onClick={() => start(false)} disabled={starting || running || health?.available === false} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="final-cut-start-btn">
            {starting || running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Scissors size={14} className="mr-1" />}
            {running ? 'Cutting…' : fc.status === 'completed' ? 'Re-cut (new version)' : 'Make the Final Cut'}
          </Button>
        </div>
      </div>
      <CutStatus fc={fc} running={running} onRetry={() => start(false)} />
      <MissingDialog missing={missing} onClose={() => setMissing(null)} onSkip={() => start(true)} onRender={() => { setMissing(null); onGoToStep(5); }} />
    </div>
  );
}
